import { Injectable } from '@angular/core';
import { retryFailedCall } from '@common';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { ErrorType, GetResponse, VitoguideBackendSettingsApiPicker } from '@vi-iot/client';
import { cloneDeep } from 'lodash-es';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { TutorialsActions } from './tutorials.actions';
import { initialTutorialsData, TutorialsData } from './tutorials.model';
import { TutorialsStore } from './tutorials.store';

@Injectable()
export class TutorialsEffects {
  constructor(
    private actions$: Actions,
    private settingApiPicker: VitoguideBackendSettingsApiPicker,
    private tutorialsStore: TutorialsStore
  ) {}

  private static TUTORIALS_STORAGE_KEY = 'tutorials';

  fetchTutorials$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(TutorialsActions.fetchTutorials.attempt),
      switchMap(() =>
        this.settingApiPicker
          .instance()
          .getKeyValueStorage(TutorialsEffects.TUTORIALS_STORAGE_KEY)
          .pipe(
            retryFailedCall(3),
            map((response: GetResponse<TutorialsData[]>) =>
              TutorialsActions.fetchTutorials.success({
                tutorials: response.data ?? initialTutorialsData,
              })
            ),
            catchError((error) => {
              if (error.errorType === ErrorType.RESOURCE_NOT_FOUND) {
                return of(
                  TutorialsActions.fetchTutorials.success({
                    tutorials: initialTutorialsData,
                  })
                );
              }

              return of(TutorialsActions.fetchTutorials.error({ error }));
            })
          )
      )
    );
  });

  saveTutorials$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(TutorialsActions.saveTutorials.attempt),
      switchMap((action) => {
        const payload: GetResponse<TutorialsData[]> = {
          data: action.tutorials,
        };

        return this.settingApiPicker
          .instance()
          .setKeyValueStorage(TutorialsEffects.TUTORIALS_STORAGE_KEY, payload)
          .pipe(
            map(() => TutorialsActions.saveTutorials.success()),
            catchError((error) => of(TutorialsActions.saveTutorials.error({ error })))
          );
      })
    );
  });

  markTutorialAsPresented$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(TutorialsActions.markAsPresented),
      concatLatestFrom(() => [this.tutorialsStore.state$]),
      switchMap(([action, state]) => {
        const tutorials: TutorialsData[] = cloneDeep(state.tutorials);
        tutorials.find(
          (tutorial: TutorialsData) => tutorial.tutorialType === action.tutorialType
        ).presented = true;

        return of(TutorialsActions.saveTutorials.attempt({ tutorials }));
      })
    );
  });

  resetTutorial$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(TutorialsActions.resetTutorial),
      concatLatestFrom(() => [this.tutorialsStore.state$]),
      switchMap(([action, state]) => {
        const tutorials: TutorialsData[] = cloneDeep(state.tutorials);
        tutorials.find(
          (tutorial: TutorialsData) => tutorial.tutorialType === action.tutorialType
        ).presented = false;

        return of(TutorialsActions.saveTutorials.attempt({ tutorials }));
      })
    );
  });
}
